"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.LabelsPanel = void 0;
const tslib_1 = require("tslib");
const client_1 = require("@apollo/client");
const catalog_core_1 = require("@tableau/catalog-core");
const catalog_messages_1 = require("@tableau/catalog-messages");
const shared_urls_1 = require("@tableau/shared-urls");
const vizportalclient_common_components_1 = require("@tableau/vizportalclient-common-components");
const widgets_1 = require("@tableau/widgets");
const react_query_1 = require("@tanstack/react-query");
const react_1 = tslib_1.__importDefault(require("react"));
const ColumnDescriptionDialog_1 = require("../description/ColumnDescriptionDialog");
const ErrorCatcher_1 = require("../error/components/ErrorCatcher");
const useToggle_1 = require("../hooks/useToggle");
const useRenderLabels_1 = require("../labels/hook/useRenderLabels");
const LabelHelpers_1 = require("./LabelHelpers");
const Styles_1 = require("./Styles");
const labelsPanelTestId = 'Labels-Panel';
function useLabelsGrid(_a) {
    var _b;
    var { elevatedPanel, probablyOrEager, renderMode } = _a, labelsParams = tslib_1.__rest(_a, ["elevatedPanel", "probablyOrEager", "renderMode"]);
    const siteUrls = (0, catalog_core_1.useSiteUrls)();
    return (0, useRenderLabels_1.useRenderLabelsForPanel)(Object.assign(Object.assign({}, labelsParams), { elevated: elevatedPanel, renderMode: renderMode, probablyOrEager: probablyOrEager === 'eager' ? 'eager' : Object.assign(Object.assign({}, probablyOrEager), { resolve: elevatedPanel }), controls: {
            kind: 'chip',
            siteUrls: (labelsParams.controls && 'siteUrls' in labelsParams.controls)
                ? (_b = labelsParams.controls) === null || _b === void 0 ? void 0 : _b.siteUrls
                : siteUrls,
        } }));
}
function LabelsPanel(props) {
    const siteUrls = new shared_urls_1.SiteUrls({ siteUrlName: props.siteUrlName });
    const queryClient = react_1.default.useMemo(() => (0, catalog_core_1.queryClientGenerator)(), []);
    const restParams = { siteLuid: props.siteLuid, baseUrl: window.location.origin };
    return (react_1.default.createElement(vizportalclient_common_components_1.RestApiServiceProvider, { params: restParams },
        react_1.default.createElement(react_query_1.QueryClientProvider, { client: queryClient },
            react_1.default.createElement(client_1.ApolloProvider, { client: props.apolloClient },
                react_1.default.createElement(catalog_core_1.SiteUrlsProvider, { value: siteUrls },
                    react_1.default.createElement(ErrorCatcher_1.ErrorCatcher, null,
                        react_1.default.createElement(LabelsPanelInner, Object.assign({}, props))))))));
}
exports.LabelsPanel = LabelsPanel;
function LabelsPanelInner(props) {
    const { element: labelsGridElement, panel } = useLabelsGrid(props);
    const isPopoverMode = props.renderMode === 'popover';
    return (react_1.default.createElement(react_1.default.Fragment, null,
        react_1.default.createElement(widgets_1.DialogWidget, { title: props.content.contentName || '', testId: labelsPanelTestId, isModal: !isPopoverMode, anchorEl: props.anchorEl, anchorOrigin: {
                vertical: widgets_1.VerticalOriginOption.top,
                horizontal: widgets_1.HorizontalOriginOption.right,
            }, showCloseButton: false, isGlassTransparent: true, shouldReturnFocusOnClose: true, onCloseRequest: props.close, handleGlassClick: props.close, type: isPopoverMode ? widgets_1.DialogType.Uber : widgets_1.DialogType.NormalWithoutTitleBar, positioningBehavior: isPopoverMode ? widgets_1.DialogPositioningBehavior.Anchored : widgets_1.DialogPositioningBehavior.Draggable, disableAutoFocus: true },
            react_1.default.createElement("div", { className: panel ? Styles_1.LabelsDoublePanelClass : Styles_1.LabelsSinglePanelClass },
                react_1.default.createElement("div", { className: panel ? Styles_1.LabelsPanelLeftAreaClass : Styles_1.LabelsPanelAreaClass },
                    react_1.default.createElement(TopSection, Object.assign({}, props)),
                    react_1.default.createElement(Description, { description: props.description }),
                    react_1.default.createElement("div", { className: Styles_1.LabelsPanelInheritanceDetailsClass },
                        react_1.default.createElement(InheritanceSection, Object.assign({}, props))),
                    react_1.default.createElement("div", { className: Styles_1.LabelsPanelLabelChipsGridClass }, labelsGridElement)),
                panel ? react_1.default.createElement("div", { className: Styles_1.LabelsPanelLabelsAreaClass }, panel) : null))));
}
function TopSection({ content, ownerName, modifiedDateTime }) {
    return (react_1.default.createElement(react_1.default.Fragment, null,
        react_1.default.createElement("div", null,
            react_1.default.createElement("span", { style: { paddingRight: '4px' } }, (0, LabelHelpers_1.assetTableauIconForType)(content.subjectArea || null, content.remoteType, 16)),
            react_1.default.createElement("span", { className: Styles_1.LabelsPanelMainAssetClass }, content.contentName)),
        content.subjectArea === 'PublishedDatasource' && (react_1.default.createElement("div", { className: Styles_1.LabelsPanelMainAssetDetailsClass },
            react_1.default.createElement("div", null,
                react_1.default.createElement("span", { className: Styles_1.LabelsPanelMainAssetParamClass }, catalog_messages_1.Messages.Owner()),
                react_1.default.createElement("span", null, ownerName)),
            react_1.default.createElement("div", null,
                react_1.default.createElement("span", { className: Styles_1.LabelsPanelMainAssetParamClass }, catalog_messages_1.Messages.Modified()),
                react_1.default.createElement("span", null, modifiedDateTime))))));
}
function InheritanceSection({ content, inheritedName, parentName, parentType, isParentCertified }) {
    if ((content === null || content === void 0 ? void 0 : content.subjectArea) === 'Column' || (content === null || content === void 0 ? void 0 : content.subjectArea) === 'Field') {
        return (0, ColumnDescriptionDialog_1.InheritanceDetails)({
            inheritedColumnName: inheritedName || '',
            assetParent: {
                name: parentName !== null && parentName !== void 0 ? parentName : '',
                contentType: parentType === 'datasource' ? 'datasource' : 'table',
                isCertified: isParentCertified !== null && isParentCertified !== void 0 ? isParentCertified : false,
            },
        });
    }
    return null;
}
function Description({ description }) {
    const isLong = react_1.default.useMemo(() => { var _a; return ((_a = description === null || description === void 0 ? void 0 : description.length) !== null && _a !== void 0 ? _a : 0) > 400; }, [description === null || description === void 0 ? void 0 : description.length]);
    const [isDescriptionCollapsed, toggleDescription] = (0, useToggle_1.useToggle)(isLong);
    if (!description) {
        return null;
    }
    else if (!isLong) {
        return react_1.default.createElement("div", { className: Styles_1.LabelsPanelDescriptionClass }, description);
    }
    else {
        return (react_1.default.createElement("div", { className: Styles_1.LabelsPanelDescriptionClass },
            isDescriptionCollapsed ? description.slice(0, 400) + '...' : description,
            react_1.default.createElement("span", { onClick: () => toggleDescription(), className: Styles_1.LabelsPanelReadMoreButtonClass }, isDescriptionCollapsed ? catalog_messages_1.Messages.ReadMore() : catalog_messages_1.Messages.ShowLess())));
    }
}
//# sourceMappingURL=LabelsPanel.js.map